/**
 * Notifications Page
 * Main page for notification preferences management
 */

import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useNotificationPreferences } from './hooks/useNotificationPreferences';
import {
  NotificationsHero,
  NotificationEmailsCard,
  DeliveryPreferencesCard,
  PromoEmailsCard,
  PushPreferencesCard,
  QuietHoursCard,
} from './components';
import type { NotificationHeroStats } from './types';

export default function Notifications() {
  const { t } = useLanguage();

  const {
    preferences,
    emails,
    isLoading,
    isSaving,
    error,
    saved,
    updatePreference,
    addEmail,
    removeEmail,
    clearError,
  } = useNotificationPreferences();

  // Calculate hero stats
  const heroStats: NotificationHeroStats = {
    emailCount: emails.length,
    frequency: preferences?.emailDigestFrequency || 'daily',
    pushEnabled: preferences?.pushEnabled || false,
    quietHoursEnabled:
      preferences?.notificationWindowStart !== 0 ||
      preferences?.notificationWindowEnd !== 23,
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <NotificationsHero stats={heroStats} />

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-16 relative z-10">
        {/* Success message */}
        {saved && (
          <div className="mb-6 flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 animate-fade-in">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{t('common.saved', 'Saved')}</span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-6 flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 animate-fade-in">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm flex-1">{error}</span>
            <button
              onClick={clearError}
              className="text-sm font-medium hover:underline"
            >
              {t('common.dismiss', 'Dismiss')}
            </button>
          </div>
        )}

        {/* Cards Grid */}
        <div className="space-y-6">
          {/* Notification Emails */}
          <div className="animate-fade-in">
            <NotificationEmailsCard
              emails={emails}
              isLoading={isSaving}
              onAddEmail={addEmail}
              onRemoveEmail={removeEmail}
            />
          </div>

          {/* Delivery Preferences */}
          <div className="animate-fade-in animation-delay-100">
            <DeliveryPreferencesCard
              emailEnabled={preferences?.emailEnabled || false}
              emailFrequency={preferences?.emailDigestFrequency || 'daily'}
              isLoading={isSaving}
              onToggleEmail={value => updatePreference('emailEnabled', value)}
              onChangeFrequency={value => updatePreference('emailDigestFrequency', value)}
            />
          </div>

          {/* Promotional Emails */}
          <div className="animate-fade-in animation-delay-200">
            <PromoEmailsCard
              enabled={preferences?.promoEmailsEnabled || false}
              isLoading={isSaving}
              onToggle={value => updatePreference('promoEmailsEnabled', value)}
            />
          </div>

          {/* Push Notifications */}
          <div className="animate-fade-in animation-delay-100">
            <PushPreferencesCard
              enabled={preferences?.pushEnabled || false}
              isLoading={isSaving}
              onToggle={value => updatePreference('pushEnabled', value)}
            />
          </div>

          {/* Quiet Hours */}
          <div className="animate-fade-in animation-delay-200">
            <QuietHoursCard
              timezone={preferences?.timezone || 'America/New_York'}
              windowStart={preferences?.notificationWindowStart || 8}
              windowEnd={preferences?.notificationWindowEnd || 22}
              isLoading={isSaving}
              onChangeTimezone={value => updatePreference('timezone', value)}
              onChangeWindowStart={value => updatePreference('notificationWindowStart', value)}
              onChangeWindowEnd={value => updatePreference('notificationWindowEnd', value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
